import * as Sentry from '@sentry/nextjs';
import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  useMediaQuery,
  styled,
} from '@mui/material';
import HeaderBar from '@/common/layouts/HeaderBar';
import DefaultLayout from '@/common/layouts/DefaultLayout';
import Division from '@/common/components/Division';
import { getServerSidePropsPrefetch } from '@/common/utils/ssr';
import { ssrMenuItems } from '@/common/hooks/useMenuItems';
import Footer from '@/common/layouts/Footer';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { useApiContext } from '@/common/contexts/ApiContext';
import { useSnackbar } from 'notistack';
import { useUserContext } from '@/common/contexts/UserContext';
import * as yup from 'yup';
import { useGlobalContext } from '@/common/contexts/GlobalContext';

interface ForgotPasswordFormFields {
  email: string;
  name: string;
  message: string;
}

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required'),
  name: yup.string().required(),
  message: yup.string().max(140),
  // phone: yup
  //   .string()
  //   .matches(phoneRegExp, "Phone number is not valid")
  //   .required(),
  // company: yup.string().required(),
});

export default function Page() {
  const { siteName, siteLogoURL, siteTagline, siteTaglineShort, contactEmail } = useGlobalContext()
  const { enqueueSnackbar } = useSnackbar();
  const matches = useMediaQuery('(min-width:768px)');
  const { apiClient } = useApiContext();
  const { user, isLoggedIn } = useUserContext();
  const submitContactFormMutation = useMutation(apiClient.users.contactUs);
  const email = user?.email ?? ''
  const name = user?.name ?? ''

  const formik = useFormik({
    initialValues: {
      email: isLoggedIn ? email : '',
      name: isLoggedIn ? name : '',
      message: '',
    },
    validationSchema: validationSchema,
    onSubmit: values => submitContactFormMutation.mutate(values),
  });

  useEffect(() => {
    if (submitContactFormMutation.isSuccess) {
      enqueueSnackbar('Thanks for contacting us!', {
        variant: 'success',
      });
      formik.setSubmitting(false);
    }

    if (submitContactFormMutation.isError) {
      enqueueSnackbar(`Something went wrong. Try to reach manually to ${contactEmail}`, { variant: 'error' });
      Sentry.captureException(submitContactFormMutation.error);
      formik.setSubmitting(false);
    }
  }, [
    submitContactFormMutation.isSuccess,
    submitContactFormMutation.isError,
  ]);

  return (
    <>
      <HeaderBar />
      <DefaultLayout>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            display="flex"
            flexDirection={matches ? 'row' : 'column'}
            justifyContent="center"
            alignItems="center"
            marginTop={2}
            marginBottom={2}
            sx={{
              background: '#24262D',
              borderRadius: '20px',
              position: 'relative',
              maxWidth: '1000px',
            }}
            boxShadow={3}
            style={{
              padding: matches ? '32px' : '32px',
              width: matches ? '80%' : '100%',
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap={3} // This replaces `gridGap={24}`; 24 pixels / 8 = 3 for theme spacing
              sx={{
                width: { xs: '100%', sm: '60%' }, // Use responsive values with the breakpoints
                order: { xs: 2, sm: 1 }, // Define the order for different breakpoints
                marginTop: { xs: '32px', sm: 0 }, // Margin top based on breakpoint
              }}
            >
              <Typography variant="h4">Contact Us</Typography>
              <Typography>
                Send us a message and we will get back to you as soon as
                possible.
              </Typography>

              <Box component="form" onSubmit={formik.handleSubmit} sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                '.MuiOutlinedInput-input:-webkit-autofill': {
                  caretColor: '#fff',
                  borderRadius: 'inherit',
                  WebkitBoxShadow: '0 0 0 100px #3272af inset',
                  WebkitTextFillColor: '#fff',
                },
              }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="name"
                  name="name"
                  label="Your Name"
                  type="text"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  id="email"
                  name="email"
                  label="Your Email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  id="message"
                  name="message"
                  type="text"
                  label="How can we be of assistance?"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.message && Boolean(formik.errors.message)
                  }
                  helperText={formik.touched.message && formik.errors.message}
                  multiline
                  rows={4}
                />
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                  style={{ maxWidth: matches ? '25%' : '100%' }}
                >
                  SEND
                </Button>
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              style={{
                paddingLeft: matches ? '5%' : 0,
                order: matches ? 2 : 1,
              }}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <img style={{
                  maxHeight: '200px',
                }} src={siteLogoURL} />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                sx={{
                  marginTop: '24px',
                }}
              >
                <Typography sx={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  backgroundColor: '#24262D',
                  zIndex: 1,
                }}>{siteName}</Typography>
                <Typography sx={{
                  marginBottom: '8px',
                  fontWeight: 'bold',
                }}>
                  {siteTaglineShort}
                </Typography>
                <Typography color="primary" sx={{
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  width: '85%',
                }}>
                  {siteTagline}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Division />
        </Box>
      </DefaultLayout>
      <Footer />
    </>
  );
}

export const getServerSideProps = getServerSidePropsPrefetch([ssrMenuItems]);
